
import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import BrandLogo from '../../../components/feature/BrandLogo';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Professional Fire Safety Solutions",
      titleAr: "حلول السلامة من الحرائق المهنية",
      subtitle: "Protecting Lives & Property with Advanced Technology",
      subtitleAr: "حماية الأرواح والممتلكات بالتكنولوجيا المتقدمة",
      image: "https://readdy.ai/api/search-image?query=Professional%20fire%20safety%20equipment%20installation%20in%20modern%20Qatar%20commercial%20building%20with%20red%20fire%20extinguishers%20sprinkler%20systems%20and%20alarm%20panels%2C%20clean%20industrial%20environment%20with%20safety%20technicians%20wearing%20protective%20gear&width=1600&height=800&seq=hero-1&orientation=landscape"
    },
    {
      title: "QCDD Approved Equipment",
      titleAr: "معدات معتمدة من QCDD",
      subtitle: "Certified Fire Protection Systems for Qatar Market",
      subtitleAr: "أنظمة حماية من الحرائق معتمدة للسوق القطري",
      image: "https://readdy.ai/api/search-image?query=Modern%20fire%20alarm%20control%20panel%20with%20digital%20display%20and%20QCDD%20certification%20in%20Qatar%20office%20building%2C%20professional%20fire%20detection%20system%20with%20red%20indicator%20lights%20and%20network%20connectivity&width=1600&height=800&seq=hero-2&orientation=landscape"
    },
    {
      title: "24/7 Emergency Support",
      titleAr: "دعم طوارئ على مدار الساعة",
      subtitle: "Rapid Response Fire Safety Services",
      subtitleAr: "خدمات السلامة من الحرائق سريعة الاستجابة",
      image: "https://readdy.ai/api/search-image?query=Emergency%20fire%20safety%20response%20team%20with%20professional%20equipment%20in%20Qatar%20industrial%20facility%2C%20fire%20safety%20technicians%20with%20red%20and%20blue%20uniforms%20providing%2024%2F7%20emergency%20support%20services&width=1600&height=800&seq=hero-3&orientation=landscape"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Subtle dark overlay for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-center"
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex justify-center items-center">
            {/* Text Content - Centered */}
            <div className="text-white space-y-6 lg:space-y-8 max-w-4xl">
              {/* Semi-transparent background for better text readability */}
              <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-6 lg:p-8">
                <BrandLogo size="large" className="text-white mb-6" />
                
                <div className="space-y-4 text-center">
                  {/* Arabic Title */}
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-white" dir="rtl">
                    {slides[currentSlide].titleAr}
                  </h1>
                  
                  {/* English Title */}
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white">
                    {slides[currentSlide].title}
                  </h1>
                  
                  {/* Arabic Subtitle */}
                  <p className="text-lg md:text-xl text-gray-100 leading-relaxed" dir="rtl">
                    {slides[currentSlide].subtitleAr}
                  </p>
                  
                  {/* English Subtitle */}
                  <p className="text-lg md:text-xl text-gray-100 leading-relaxed">
                    {slides[currentSlide].subtitle}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mt-6 justify-center">
                  <Button size="lg" className="bg-brand-red hover:bg-white hover:text-brand-red border-2 border-brand-red shadow-lg">
                    <i className="ri-fire-line mr-2"></i>
                    Our Products
                  </Button>
                  <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-brand-blue shadow-lg">
                    <i className="ri-phone-line mr-2"></i>
                    Emergency Support
                  </Button>
                </div>
              </div>

              {/* Key Features */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
                <div className="flex items-center space-x-3 bg-black/30 backdrop-blur-sm rounded-xl p-4">
                  <div className="w-12 h-12 bg-brand-red rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-white">QCDD Certified</h3>
                    <p className="text-gray-200 text-sm">Approved Equipment</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 bg-black/30 backdrop-blur-sm rounded-xl p-4">
                  <div className="w-12 h-12 bg-brand-red rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-white">24/7 Support</h3>
                    <p className="text-gray-200 text-sm">Emergency Response</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 bg-black/30 backdrop-blur-sm rounded-xl p-4">
                  <div className="w-12 h-12 bg-brand-red rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-white">Expert Team</h3>
                    <p className="text-gray-200 text-sm">Professional Service</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-brand-red scale-125' : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
